// EnhancedEstimateComponents.js - 백엔드 API와 연동된 견적서 컴포넌트들

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Stack,
  Divider,
  Alert,
  Chip,
  IconButton,
  Collapse,
  CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';

// API 서비스 import
import { 
  getEstimateForm, 
  submitEstimate, 
  cancelEstimate, 
  sendEstimateMessage,
  ESTIMATE_ACTIONS
} from '../../../lib/api/chatApi';
//import { fetchChatHistory } from '../../lib/api/chatApi';

// 기존 견적서 목록 표시 컴포넌트
const ExistingQuotesDisplay = ({ quotes }) => {
  const [expanded, setExpanded] = useState(false);

  if (!quotes || quotes.length === 0) {
    return null;
  }

  const formatCurrency = (amount) => {
    if (!amount) return '미정';
    return `${parseInt(amount).toLocaleString()}원`;
  };

  return (
    <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              📋 기존 매출 견적서 ({quotes.length}개)
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>

          <Collapse in={expanded}>
            <Stack spacing={1}>
              {quotes.map((quote, index) => (
                <Box 
                  key={quote.qteUuid || index}
                  sx={{ 
                    p: 2, 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: 1,
                    border: '1px solid #e9ecef'
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        견적서 번호
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {quote.qteNo}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        견적 금액
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {formatCurrency(quote.qtePrice)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        견적 일자
                      </Typography>
                      <Typography variant="body2">
                        {quote.qteRegDt}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        담당자
                      </Typography>
                      <Typography variant="body2">
                        {quote.qteUsrNm}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Stack>
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
};

// 견적서 등록 확인 컴포넌트 (API 연동 개선)
const EstimateRegistrationConfirm = ({ 
  data, 
  onConfirm, 
  onCancel, 
  mode = 'chat', // 'chat' 또는 'api'
  onSend // 채팅 메시지 전송용
}) => {
  const { project, existingQuotes, fileName, confirmMessage, tempQteUuid, confirmationToken } = data;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      if (mode === 'api') {
        // 직접 API 호출
        const response = await getEstimateForm(tempQteUuid, confirmationToken);
        if (onConfirm) {
          onConfirm(response);
        }
      } else {
        // 채팅 메시지 방식
        sendEstimateMessage(onSend, ESTIMATE_ACTIONS.FORM_REQUEST, tempQteUuid, null, confirmationToken);
        if (onConfirm) {
          onConfirm();
        }
      }
    } catch (error) {
      console.error('견적서 폼 요청 실패:', error);
      setError('견적서 폼을 불러오는데 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    setError(null);

    try {
      if (mode === 'api') {
        // 직접 API 호출
        const response = await cancelEstimate(tempQteUuid, confirmationToken);
        if (onCancel) {
          onCancel(response);
        }
      } else {
        // 채팅 메시지 방식
        sendEstimateMessage(onSend, ESTIMATE_ACTIONS.CANCEL, tempQteUuid, null, confirmationToken);
        if (onCancel) {
          onCancel();
        }
      }
    } catch (error) {
      console.error('견적서 취소 실패:', error);
      setError('견적서 취소에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: '100%' }}>
      {/* 에러 표시 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          {confirmMessage}
        </Typography>
      </Alert>

      {/* 타이디 카드: 프로젝트명(제목), 프로젝트번호/첨부파일은 작은 보조 텍스트로 */}
      <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
        <CardContent>
          <Stack spacing={0.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {project.cstNm}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[project.prjNo, fileName ? `첨부 ${fileName}` : null].filter(Boolean).join(' · ')}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* 기존 견적서 목록 */}
      <ExistingQuotesDisplay quotes={existingQuotes} />

      {/* 확인 버튼들 */}
      <Stack direction="row" spacing={2} justifyContent="center">
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={loading}
          sx={{ minWidth: 120 }}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? '처리 중...' : '새 견적서 등록하기'}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCancel}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          취소
        </Button>
      </Stack>
    </Box>
  );
};

// 견적서 등록 폼 컴포넌트 (API 연동 개선)
const EstimateRegistrationForm = ({ 
  data, 
  onSubmit, 
  onCancel, 
  mode = 'chat', // 'chat' 또는 'api'
  onSend // 채팅 메시지 전송용
}) => {
  const { defaultValues, tempQteUuid, confirmationToken } = data;
  const [formData, setFormData] = useState({
    qtePrice: '',
    qteUsrNm: defaultValues?.qteUsrNm || '',
    qteUsrTel: defaultValues?.qteUsrTel || '',
    qteUsrEml: defaultValues?.qteUsrEml || '',
    qteRegDt: defaultValues?.qteRegDt || '',
    qteValidDt: defaultValues?.qteValidDt || '',
    destPlace: defaultValues?.destPlace || '',
    qteCondt: defaultValues?.qteCondt || '',
    payCond: defaultValues?.payCond || '',
    mntTor: defaultValues?.mntTor || '12',
    qteDesc1: defaultValues?.qteDesc1 || '',
    qteDesc2: defaultValues?.qteDesc2 || '',
    qteDesc3: defaultValues?.qteDesc3 || ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // 에러 클리어
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.qtePrice) newErrors.qtePrice = '견적 금액은 필수입니다';
    if (!formData.qteUsrNm) newErrors.qteUsrNm = '담당자명은 필수입니다';
    if (!formData.qteRegDt) newErrors.qteRegDt = '견적 일자는 필수입니다';
    if (!formData.qteValidDt) newErrors.qteValidDt = '유효 일자는 필수입니다';
    
    // 견적 금액 숫자 검증
    if (formData.qtePrice && isNaN(formData.qtePrice.replace(/[^0-9]/g, ''))) {
      newErrors.qtePrice = '견적 금액은 숫자만 입력해주세요';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setLoading(true);
      setError(null);

      try {
        if (mode === 'api') {
          // 직접 API 호출
          const response = await submitEstimate(tempQteUuid, formData, confirmationToken);
          if (onSubmit) {
            onSubmit(response);
          }
        } else {
          // 채팅 메시지 방식
          sendEstimateMessage(onSend, ESTIMATE_ACTIONS.SUBMIT, tempQteUuid, formData, confirmationToken);
          if (onSubmit) {
            onSubmit(formData);
          }
        }
      } catch (error) {
        console.error('견적서 등록 실패:', error);
        setError('견적서 등록에 실패했습니다. 입력 내용을 확인하고 다시 시도해주세요.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    setError(null);

    try {
      if (mode === 'api') {
        // 직접 API 호출
        const response = await cancelEstimate(tempQteUuid, confirmationToken);
        if (onCancel) {
          onCancel(response);
        }
      } else {
        // 채팅 메시지 방식
        sendEstimateMessage(onSend, ESTIMATE_ACTIONS.CANCEL, tempQteUuid, null, confirmationToken);
        if (onCancel) {
          onCancel();
        }
      }
    } catch (error) {
      console.error('견적서 취소 실패:', error);
      setError('견적서 취소에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: '100%' }}>
      {/* 에러 표시 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          📝 새로운 매출 견적서 정보를 입력해주세요.
        </Typography>
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={3}>
            {/* 기본 정보 */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                💰 견적 기본 정보
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="견적 금액 *"
                    placeholder="10000000"
                    value={formData.qtePrice}
                    onChange={handleChange('qtePrice')}
                    error={!!errors.qtePrice}
                    helperText={errors.qtePrice}
                    InputProps={{
                      endAdornment: '원'
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="유지보수기간"
                    placeholder="12"
                    value={formData.mntTor}
                    onChange={handleChange('mntTor')}
                    InputProps={{
                      endAdornment: '개월'
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="견적 일자 *"
                    value={formData.qteRegDt}
                    onChange={handleChange('qteRegDt')}
                    error={!!errors.qteRegDt}
                    helperText={errors.qteRegDt}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="유효 일자 *"
                    value={formData.qteValidDt}
                    onChange={handleChange('qteValidDt')}
                    error={!!errors.qteValidDt}
                    helperText={errors.qteValidDt}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 담당자 정보 */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                👤 담당자 정보
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="담당자명 *"
                    value={formData.qteUsrNm}
                    onChange={handleChange('qteUsrNm')}
                    error={!!errors.qteUsrNm}
                    helperText={errors.qteUsrNm}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="담당자 연락처"
                    value={formData.qteUsrTel}
                    onChange={handleChange('qteUsrTel')}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="담당자 이메일"
                    type="email"
                    value={formData.qteUsrEml}
                    onChange={handleChange('qteUsrEml')}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 조건 정보 */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                📋 조건 정보
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="납품 장소"
                    value={formData.destPlace}
                    onChange={handleChange('destPlace')}
                    placeholder="고객사 사무실"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="수금 지급 조건"
                    value={formData.qteCondt}
                    onChange={handleChange('qteCondt')}
                    placeholder="계약 후 착수"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="결제 조건"
                    value={formData.payCond}
                    onChange={handleChange('payCond')}
                    placeholder="분할 지급"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* 상세 설명 */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                📝 견적서 설명
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="견적서 설명 1"
                  multiline
                  rows={2}
                  value={formData.qteDesc1}
                  onChange={handleChange('qteDesc1')}
                  placeholder="주요 제공 서비스 및 범위"
                />
                <TextField
                  fullWidth
                  size="small"
                  label="견적서 설명 2"
                  multiline
                  rows={2}
                  value={formData.qteDesc2}
                  onChange={handleChange('qteDesc2')}
                  placeholder="추가 조건 및 특이사항"
                />
                <TextField
                  fullWidth
                  size="small"
                  label="견적서 설명 3"
                  multiline
                  rows={2}
                  value={formData.qteDesc3}
                  onChange={handleChange('qteDesc3')}
                  placeholder="기타 참고사항"
                />
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* 버튼들 */}
      <Stack direction="row" spacing={2} justifyContent="center">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ minWidth: 120 }}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? '등록 중...' : '견적서 등록'}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCancel}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          취소
        </Button>
      </Stack>
    </Box>
  );
};

// 견적서 등록 완료 컴포넌트
const EstimateRegistrationComplete = ({ data }) => {
  const { registeredQuote, summaryMessage, detailUrl } = data;

  const formatCurrency = (amount) => {
    if (!amount) return '0';
    return parseInt(amount).toLocaleString();
  };

  // 타이디 카드: 견적서 번호(제목) + 등록완료 배지(오른쪽), 견적 금액(핵심 숫자)을 크게,
  // 담당자/일자/유지보수기간은 작은 보조 텍스트로. 설명 문단은 정보 손실 없이 그대로 둔다.
  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Card sx={{ border: '1px solid #4caf50' }}>
        <CardContent>
          <Stack spacing={1.25}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                견적서 {registeredQuote.qteNo}
              </Typography>
              <Chip label="등록 완료" color="success" size="small" variant="filled" />
            </Stack>

            <Box>
              <Typography variant="caption" color="text.secondary">견적 금액</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {formatCurrency(registeredQuote.qtePrice)}원
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary">
              {[
                registeredQuote.qteUsrNm ? `담당 ${registeredQuote.qteUsrNm}` : null,
                registeredQuote.qteRegDt ? `견적일 ${registeredQuote.qteRegDt}` : null,
                registeredQuote.qteValidDt ? `유효일 ${registeredQuote.qteValidDt}` : null,
                registeredQuote.mntTor ? `유지보수 ${registeredQuote.mntTor}개월` : null,
              ].filter(Boolean).join(' · ')}
            </Typography>

            {summaryMessage && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                {summaryMessage}
              </Typography>
            )}

            {/* 설명 정보 */}
            {(registeredQuote.qteDesc1 || registeredQuote.qteDesc2 || registeredQuote.qteDesc3) && (
              <>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    📝 견적서 설명
                  </Typography>
                  <Stack spacing={1}>
                    {registeredQuote.qteDesc1 && (
                      <Typography variant="body2" sx={{
                        backgroundColor: '#f8f9fa',
                        p: 1,
                        borderRadius: 1,
                        fontSize: '0.875rem'
                      }}>
                        • {registeredQuote.qteDesc1}
                      </Typography>
                    )}
                    {registeredQuote.qteDesc2 && (
                      <Typography variant="body2" sx={{
                        backgroundColor: '#f8f9fa',
                        p: 1,
                        borderRadius: 1,
                        fontSize: '0.875rem'
                      }}>
                        • {registeredQuote.qteDesc2}
                      </Typography>
                    )}
                    {registeredQuote.qteDesc3 && (
                      <Typography variant="body2" sx={{
                        backgroundColor: '#f8f9fa',
                        p: 1,
                        borderRadius: 1,
                        fontSize: '0.875rem'
                      }}>
                        • {registeredQuote.qteDesc3}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* 다음 액션 안내 */}
      <Stack sx={{ mt: 2 }} spacing={1.5} alignItems="center">
        {detailUrl && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<LaunchIcon />}
            onClick={() => {
              const nextUrl = detailUrl.startsWith('/')
                ? `${window.location.origin}${detailUrl}`
                : detailUrl;
              window.open(nextUrl, '_blank', 'noopener,noreferrer');
            }}
            sx={{ textTransform: 'none' }}
          >
            견적 화면 열기
          </Button>
        )}
        <Typography variant="caption" color="text.secondary">
          💡 견적서 수정이나 추가 작업이 필요하시면 언제든 말씀해주세요!
        </Typography>
      </Stack>
    </Box>
  );
};

export { 
  ExistingQuotesDisplay, 
  EstimateRegistrationConfirm, 
  EstimateRegistrationForm, 
  EstimateRegistrationComplete 
};
